import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FinancialConfig } from './FinancialConfig.js';
import { formatInputValue, getNumericInputProps } from './utils/inputHelpers.js';
import CalculationTransparency from './components/CalculationTransparency.js';

const SMSFGovernmentCoContribution = ({ smsfState, setSmsfState }) => {
    const [activeTab, setActiveTab] = useState('calculator');
    
    // Co-contribution data
    const [coContribData, setCoContribData] = useState({
        assessableIncome: 45000,
        totalIncome: 45000,
        nonConcessionalContributions: 1000,
        age: 45,
        employmentStatus: 'employed', // employed, self-employed, unemployed
        superBalance: 300000,
        hasNonConcessionalContributions: true
    });

    const [results, setResults] = useState(null);

    // Government Co-contribution constants from FinancialConfig
    const CO_CONTRIBUTION = FinancialConfig.CONTRIBUTION_CAPS.CO_CONTRIBUTION;

    // Calculate co-contribution
    const calculateCoContribution = () => {
        const errors = [];
        const warnings = [];

        // Age eligibility
        if (coContribData.age >= CO_CONTRIBUTION.AGE_LIMIT) {
            errors.push({
                type: 'age',
                message: `Must be under ${CO_CONTRIBUTION.AGE_LIMIT} years old on 30 June of the financial year`,
                value: coContribData.age
            });
        }

        // Income threshold check
        const income = coContribData.totalIncome;
        let coContributionAmount = 0;
        let eligible = true;
        let reason = null;

        if (income < CO_CONTRIBUTION.MIN_INCOME_THRESHOLD) {
            // Maximum co-contribution
            coContributionAmount = Math.min(
                coContribData.nonConcessionalContributions * CO_CONTRIBUTION.MATCHING_RATE,
                CO_CONTRIBUTION.MAX_AMOUNT
            );
        } else if (income <= CO_CONTRIBUTION.MAX_INCOME_THRESHOLD) {
            // Graduated co-contribution
            const incomeAboveThreshold = income - CO_CONTRIBUTION.MIN_INCOME_THRESHOLD;
            const reductionRate = CO_CONTRIBUTION.MAX_AMOUNT / 
                (CO_CONTRIBUTION.MAX_INCOME_THRESHOLD - CO_CONTRIBUTION.MIN_INCOME_THRESHOLD);
            const maxEligible = CO_CONTRIBUTION.MAX_AMOUNT - (incomeAboveThreshold * reductionRate);
            
            coContributionAmount = Math.min(
                coContribData.nonConcessionalContributions * CO_CONTRIBUTION.MATCHING_RATE,
                maxEligible
            );
        } else {
            // Above threshold - not eligible
            eligible = false;
            reason = `Total income ($${income.toLocaleString()}) exceeds threshold ($${CO_CONTRIBUTION.MAX_INCOME_THRESHOLD.toLocaleString()})`;
        }

        // Check if made eligible contributions
        if (coContribData.nonConcessionalContributions === 0) {
            eligible = false;
            reason = 'No personal non-concessional contributions made';
        }

        // Employment requirement (need to have income from employment or business)
        if (coContribData.employmentStatus === 'unemployed' && coContribData.totalIncome <= 0) {
            warnings.push({
                type: 'employment',
                message: 'At least 10% of total income must come from employment or carrying on a business',
                severity: 'warning'
            });
        }

        // TFN lodgement requirement
        warnings.push({
            type: 'tfn',
            message: 'Must have lodged a tax return for the financial year',
            severity: 'info'
        });

        // Optimal contribution calculation
        const optimalContribution = calculateOptimalContribution(income);

        // Lifetime value
        const lifetimeValue = calculateLifetimeValue(coContributionAmount);

        setResults({
            eligible,
            reason,
            coContributionAmount: Math.round(coContributionAmount),
            personalContribution: coContribData.nonConcessionalContributions,
            totalContribution: coContribData.nonConcessionalContributions + Math.round(coContributionAmount),
            effectiveReturn: coContribData.nonConcessionalContributions > 0 
                ? (coContributionAmount / coContribData.nonConcessionalContributions) * 100 
                : 0,
            income: income,
            incomeThreshold: {
                min: CO_CONTRIBUTION.MIN_INCOME_THRESHOLD,
                max: CO_CONTRIBUTION.MAX_INCOME_THRESHOLD
            },
            errors,
            warnings,
            optimalContribution,
            lifetimeValue
        });
        setActiveTab('results');
    };

    const calculateOptimalContribution = (income) => {
        if (income >= CO_CONTRIBUTION.MAX_INCOME_THRESHOLD) {
            return {
                amount: 0,
                coContrib: 0,
                total: 0,
                message: 'Not eligible - income too high'
            };
        }

        let maxCoContrib = 0;
        if (income < CO_CONTRIBUTION.MIN_INCOME_THRESHOLD) {
            maxCoContrib = CO_CONTRIBUTION.MAX_AMOUNT;
        } else {
            const incomeAboveThreshold = income - CO_CONTRIBUTION.MIN_INCOME_THRESHOLD;
            const reductionRate = CO_CONTRIBUTION.MAX_AMOUNT / 
                (CO_CONTRIBUTION.MAX_INCOME_THRESHOLD - CO_CONTRIBUTION.MIN_INCOME_THRESHOLD);
            maxCoContrib = CO_CONTRIBUTION.MAX_AMOUNT - (incomeAboveThreshold * reductionRate);
        }

        // To get maximum co-contribution, need to contribute enough
        const optimalPersonalContrib = Math.ceil(maxCoContrib / CO_CONTRIBUTION.MATCHING_RATE);

        return {
            amount: optimalPersonalContrib,
            coContrib: Math.round(maxCoContrib),
            total: optimalPersonalContrib + Math.round(maxCoContrib),
            message: `Contribute $${optimalPersonalContrib.toLocaleString()} to receive maximum government co-contribution of $${Math.round(maxCoContrib).toLocaleString()}`
        };
    };

    const calculateLifetimeValue = (coContrib) => {
        // Assuming 30 years to retirement, 7% annual return
        const years = 30;
        const returnRate = 0.07;
        const futureValue = coContrib * Math.pow(1 + returnRate, years);

        return {
            contribution: coContrib,
            years,
            returnRate: returnRate * 100,
            futureValue: Math.round(futureValue),
            growth: Math.round(futureValue - coContrib)
        };
    };

    useEffect(() => {
        calculateCoContribution();
    }, [coContribData]);

    const renderCalculator = () => (
        <div className="space-y-6">
            {/* Eligibility Checklist */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                <h3 className="font-semibold text-blue-900 mb-2">✓ Eligibility Requirements</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Be under age {CO_CONTRIBUTION.AGE_LIMIT} on 30 June of the financial year</li>
                    <li>• Make personal non-concessional (after-tax) contributions</li>
                    <li>• Total income less than ${CO_CONTRIBUTION.MAX_INCOME_THRESHOLD.toLocaleString()}</li>
                    <li>• At least 10% of income from employment or business</li>
                    <li>• Lodge a tax return for the financial year</li>
                    <li>• Have a total super balance below the general transfer balance cap (${(FinancialConfig.TRANSFER_BALANCE_CAP / 1e6).toFixed(1)}M)</li>
                </ul>
            </div>

            {/* Personal Details */}
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-semibold text-lg mb-4">Personal Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Age (as at 30 June)
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={coContribData.age}
                            onChange={(e) => setCoContribData({ ...coContribData, age: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Employment Status
                        </label>
                        <select
                            value={coContribData.employmentStatus}
                            onChange={(e) => setCoContribData({ ...coContribData, employmentStatus: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        >
                            <option value="employed">Employed</option>
                            <option value="self-employed">Self-Employed</option>
                            <option value="unemployed">Unemployed</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Income Details */}
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-semibold text-lg mb-4">Income Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Total Income (for eligibility)
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={coContribData.totalIncome}
                            onChange={(e) => setCoContribData({ ...coContribData, totalIncome: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Assessable income + reportable fringe benefits + reportable employer super
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Total Super Balance
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={coContribData.superBalance}
                            onChange={(e) => setCoContribData({ ...coContribData, superBalance: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                </div>
            </div>

            {/* Contribution Details */}
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-semibold text-lg mb-4">Contribution Details</h3>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Personal Non-Concessional Contributions
                    </label>
                    <input
                        {...getNumericInputProps()}
                        value={coContribData.nonConcessionalContributions}
                        onChange={(e) => setCoContribData({ ...coContribData, nonConcessionalContributions: Number(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        After-tax contributions you've made (maximum eligible: ${CO_CONTRIBUTION.MAX_ELIGIBLE_CONTRIBUTION.toLocaleString()})
                    </p>
                </div>
            </div>

            <button
                type="button"
                onClick={calculateCoContribution}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 font-semibold"
            >
                Calculate Co-Contribution
            </button>
        </div>
    );

    const renderResults = () => {
        if (!results) return null;

        return (
            <div className="space-y-6">
                {/* Eligibility Status */}
                {results.eligible ? (
                    <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-lg">
                        <h3 className="font-semibold text-green-900 text-lg mb-2">✅ Eligible for Co-Contribution!</h3>
                        <p className="text-green-800">
                            Based on your income and contributions, you're eligible for the government co-contribution.
                        </p>
                    </div>
                ) : (
                    <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg">
                        <h3 className="font-semibold text-red-900 text-lg mb-2">❌ Not Eligible</h3>
                        <p className="text-red-800">{results.reason}</p>
                    </div>
                )}

                {/* Results Cards */}
                {results.eligible && (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-white p-6 rounded-lg shadow border-l-4 border-blue-500">
                                <div className="text-sm text-gray-600 mb-1">Your Contribution</div>
                                <div className="text-2xl font-bold text-blue-600">
                                    ${results.personalContribution.toLocaleString()}
                                </div>
                            </div>

                            <div className="bg-white p-6 rounded-lg shadow border-l-4 border-green-500">
                                <div className="text-sm text-gray-600 mb-1">Government Co-Contribution</div>
                                <div className="text-2xl font-bold text-green-600">
                                    ${results.coContributionAmount.toLocaleString()}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                    Free money!
                                </div>
                            </div>

                            <div className="bg-white p-6 rounded-lg shadow border-l-4 border-purple-500">
                                <div className="text-sm text-gray-600 mb-1">Total Contribution</div>
                                <div className="text-2xl font-bold text-purple-600">
                                    ${results.totalContribution.toLocaleString()}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                    {results.effectiveReturn.toFixed(0)}% instant return!
                                </div>
                            </div>
                        </div>

                        {/* Strategy Analysis */}
                        {results.optimalContribution.amount > results.personalContribution && (
                            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-lg">
                                <h3 className="font-semibold text-yellow-900 mb-2">💡 Optimization Opportunity</h3>
                                <p className="text-yellow-800 mb-3">{results.optimalContribution.message}</p>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span className="text-yellow-700">Additional Contribution Needed:</span>
                                        <span className="ml-2 font-semibold">
                                            ${(results.optimalContribution.amount - results.personalContribution).toLocaleString()}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="text-yellow-700">Additional Co-Contribution:</span>
                                        <span className="ml-2 font-semibold text-green-600">
                                            ${(results.optimalContribution.coContrib - results.coContributionAmount).toLocaleString()}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Lifetime Value */}
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg shadow">
                            <h3 className="font-semibold text-lg mb-4">🌟 Lifetime Value</h3>
                            <p className="text-gray-700 mb-4">
                                With compound growth over {results.lifetimeValue.years} years at {results.lifetimeValue.returnRate}% p.a., 
                                your ${results.lifetimeValue.contribution} government co-contribution could grow to:
                            </p>
                            <div className="text-4xl font-bold text-green-600 mb-2">
                                ${results.lifetimeValue.futureValue.toLocaleString()}
                            </div>
                            <div className="text-sm text-gray-600">
                                That's ${results.lifetimeValue.growth.toLocaleString()} in investment growth!
                            </div>
                        </div>

                        {/* Income Threshold Chart */}
                        <div className="bg-white p-6 rounded-lg shadow">
                            <h4 className="font-semibold mb-4">Income vs Co-Contribution</h4>
                            <div className="relative pt-1">
                                <div className="flex mb-2 items-center justify-between">
                                    <div className="text-xs text-gray-600">
                                        ${results.incomeThreshold.min.toLocaleString()}
                                    </div>
                                    <div className="text-xs text-gray-600">
                                        Your Income: ${results.income.toLocaleString()}
                                    </div>
                                    <div className="text-xs text-gray-600">
                                        ${results.incomeThreshold.max.toLocaleString()}
                                    </div>
                                </div>
                                <div className="overflow-hidden h-4 mb-4 text-xs flex rounded bg-gray-200">
                                    <div 
                                        className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-gradient-to-r from-green-500 to-red-500"
                                        style={{ 
                                            width: `${((results.income - results.incomeThreshold.min) / (results.incomeThreshold.max - results.incomeThreshold.min)) * 100}%` 
                                        }}
                                    />
                                </div>
                                <div className="flex justify-between text-xs text-gray-600">
                                    <span>Max: $500</span>
                                    <span>You get: ${results.coContributionAmount}</span>
                                    <span>Min: $0</span>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* Warnings */}
                {results.warnings.length > 0 && (
                    <div className="bg-white p-6 rounded-lg shadow">
                        <h4 className="font-semibold mb-3">⚠️ Important Notes</h4>
                        <div className="space-y-2">
                            {results.warnings.map((warning, index) => (
                                <div key={index} className="text-sm text-gray-700 flex items-start">
                                    <span className="mr-2">•</span>
                                    <span>{warning.message}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Errors */}
                {results.errors.length > 0 && (
                    <div className="bg-red-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-red-900 mb-2">Eligibility Issues:</h4>
                        <ul className="space-y-1">
                            {results.errors.map((error, index) => (
                                <li key={index} className="text-sm text-red-800">• {error.message}</li>
                            ))}
                        </ul>
                    </div>
                )}

                <CalculationTransparency
                    kind="smsf"
                    title="Government Co-Contribution Transparency"
                    inputs={{
                        currentIncome: coContribData.totalIncome,
                        contributionAmount: coContribData.personalContribution,
                        totalBalance: coContribData.totalSuperBalance,
                    }}
                    results={{
                        ...results,
                        recommendations: results.optimalContribution ? [results.optimalContribution] : [],
                    }}
                />
            </div>
        );
    };

    const renderEducation = () => (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-semibold text-lg mb-4">📚 About Government Co-Contribution</h3>
                
                <div className="space-y-4">
                    <div>
                        <h4 className="font-semibold text-gray-900 mb-2">What is it?</h4>
                        <p className="text-gray-700">
                            The government co-contribution is free money added to your super when you make personal 
                            after-tax contributions and meet eligibility requirements. The government matches your 
                            contributions at 50 cents per dollar, up to a maximum of $500.
                        </p>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-green-900 mb-2">Key Benefits</h4>
                        <ul className="list-disc list-inside space-y-1 text-green-800">
                            <li>50% instant return on your contribution (up to $500)</li>
                            <li>Automatic - no application needed</li>
                            <li>Compounds over time for retirement</li>
                            <li>Particularly valuable for low-to-middle income earners</li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="font-semibold text-gray-900 mb-2">How It Works</h4>
                        <div className="bg-blue-50 p-4 rounded-lg space-y-2 text-sm">
                            <p><strong>Step 1:</strong> Make personal non-concessional (after-tax) contributions to your super</p>
                            <p><strong>Step 2:</strong> Total income would need to be below ${CO_CONTRIBUTION.MAX_INCOME_THRESHOLD.toLocaleString()} for eligibility</p>
                            <p><strong>Step 3:</strong> Lodge your tax return</p>
                            <p><strong>Step 4:</strong> ATO automatically calculates and pays co-contribution to your super fund</p>
                        </div>
                    </div>

                    <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Income Thresholds (2024-25)</h4>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b">
                                        <th className="text-left py-2">Total Income</th>
                                        <th className="text-right py-2">Your $1,000</th>
                                        <th className="text-right py-2">Government Adds</th>
                                        <th className="text-right py-2">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="border-b">
                                        <td className="py-2">${CO_CONTRIBUTION.MIN_INCOME_THRESHOLD.toLocaleString()} or less</td>
                                        <td className="text-right">$1,000</td>
                                        <td className="text-right text-green-600 font-semibold">$500</td>
                                        <td className="text-right font-semibold">$1,500</td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="py-2">$55,000</td>
                                        <td className="text-right">$1,000</td>
                                        <td className="text-right text-green-600 font-semibold">$250</td>
                                        <td className="text-right font-semibold">$1,250</td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="py-2">${CO_CONTRIBUTION.MAX_INCOME_THRESHOLD.toLocaleString()} or more</td>
                                        <td className="text-right">$1,000</td>
                                        <td className="text-right text-red-600">$0</td>
                                        <td className="text-right">$1,000</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-yellow-900 mb-2">⚠️ Important Notes</h4>
                        <ul className="list-disc list-inside space-y-1 text-yellow-800 text-sm">
                            <li>Only available on personal non-concessional contributions</li>
                            <li>Employer contributions don't count</li>
                            <li>Salary sacrifice contributions don't count</li>
                            <li>Must be under age {CO_CONTRIBUTION.AGE_LIMIT} on 30 June</li>
                            <li>At least 10% of income must be from employment or business</li>
                            <li>Total super balance must be below the general transfer balance cap (${(FinancialConfig.TRANSFER_BALANCE_CAP / 1e6).toFixed(1)}M)</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="max-w-7xl mx-auto p-6">
            {/* Header */}
            <div className="mb-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Government Co-Contribution Calculator</h2>
                <p className="text-gray-600">
                    Calculate your eligibility for free government super contributions
                </p>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 mb-6">
                <nav className="flex space-x-8">
                    {[
                        { id: 'calculator', label: 'Calculator', icon: '🧮' },
                        { id: 'results', label: 'Results', icon: '📊' },
                        { id: 'education', label: 'How It Works', icon: '📚' }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`py-4 px-1 border-b-2 font-medium text-sm ${
                                activeTab === tab.id
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            <span className="mr-2">{tab.icon}</span>
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Content */}
            <div>
                {activeTab === 'calculator' && renderCalculator()}
                {activeTab === 'results' && renderResults()}
                {activeTab === 'education' && renderEducation()}
            </div>
        </div>
    );
};

SMSFGovernmentCoContribution.propTypes = {
    smsfState: PropTypes.object.isRequired,
    setSmsfState: PropTypes.func.isRequired
};

export default SMSFGovernmentCoContribution;

